import { CurriculumStructure, Topic, LearningPath, ContentIndex, SearchFilter, SearchResult } from '../types/curriculum';
import { Lesson } from '../types/education';
import { colorTheoryCurriculum } from '../data/curriculum/topicTaxonomy';

// Map existing lesson IDs to new topic taxonomy
export const lessonToTopicMapping: Record<string, string[]> = {
  'color-basics': ['foundations', 'color-wheel', 'color-properties'],
  'color-wheel': ['color-wheel'],
  'harmonies': ['harmony', 'complementary-harmony', 'analogous-harmony', 'triadic-harmony'],
  'color-psychology': ['psychology', 'emotional-impact', 'cultural-significance'],
  'accessibility': ['application', 'accessibility'],
  'advanced-techniques': ['application', 'digital-design', 'color-workflows']
};

// Map existing lesson categories to topic taxonomy
export const categoryToTopicMapping: Record<string, string> = {
  'Foundations': 'foundations',
  'Harmony': 'harmony', 
  'Psychology': 'psychology',
  'Application': 'application'
};

export class CurriculumService {
  private curriculum: CurriculumStructure;
  private lessons: Lesson[];

  constructor(lessons: Lesson[]) {
    this.curriculum = colorTheoryCurriculum;
    this.lessons = lessons;
  }

  // Topic Management
  getTopic(id: string): Topic | undefined {
    return this.curriculum.topics[id];
  }

  getAllTopics(): Topic[] {
    return Object.values(this.curriculum.topics);
  }

  getTopicsByLevel(level: number): Topic[] {
    return this.getAllTopics().filter(topic => topic.level === level);
  }

  getTopicsByParent(parentId?: string): Topic[] {
    return this.getAllTopics().filter(topic => topic.parentId === parentId);
  }

  getTopicHierarchy(): Topic[] {
    const rootTopics = this.getTopicsByLevel(0);
    return rootTopics.map(topic => this.buildTopicTree(topic));
  }

  private buildTopicTree(topic: Topic): Topic {
    const children = this.getTopicsByParent(topic.id);
    return {
      ...topic,
      children: children.map(child => this.buildTopicTree(child))
    } as Topic & { children: Topic[] };
  }

  // Learning Path Management
  getLearningPath(id: string): LearningPath | undefined {
    return this.curriculum.learningPaths[id];
  }

  getAllLearningPaths(): LearningPath[] {
    return Object.values(this.curriculum.learningPaths);
  }

  getLearningPathsByDifficulty(difficulty: 'beginner' | 'intermediate' | 'advanced'): LearningPath[] {
    return this.getAllLearningPaths().filter(path => path.difficulty === difficulty);
  }

  getLearningPathsByCategory(category: string): LearningPath[] {
    return this.getAllLearningPaths().filter(path => path.category === category);
  }

  // Lesson-Topic Integration
  getTopicsForLesson(lessonId: string): Topic[] {
    const topicIds = lessonToTopicMapping[lessonId] || [];
    return topicIds.map(id => this.getTopic(id)).filter(Boolean) as Topic[];
  }

  getLessonsForTopic(topicId: string): Lesson[] {
    return this.lessons.filter(lesson => 
      lessonToTopicMapping[lesson.id]?.includes(topicId)
    );
  }

  getLessonsForLearningPath(pathId: string): Lesson[] {
    const path = this.getLearningPath(pathId);
    if (!path) return [];

    const lessons: Lesson[] = [];
    path.topics.forEach(topicId => {
      const topicLessons = this.getLessonsForTopic(topicId);
      lessons.push(...topicLessons);
    });

    // Remove duplicates and maintain order
    return Array.from(new Map(lessons.map(lesson => [lesson.id, lesson])).values());
  }

  // Prerequisite Management
  getTopicPrerequisites(topicId: string): Topic[] {
    const topic = this.getTopic(topicId);
    if (!topic) return [];

    return topic.prerequisites.map(id => this.getTopic(id)).filter(Boolean) as Topic[];
  }

  canAccessTopic(topicId: string, completedTopics: string[] = []): boolean {
    const prerequisites = this.getTopicPrerequisites(topicId);
    return prerequisites.every(prereq => completedTopics.includes(prereq.id));
  }

  getAvailableTopics(completedTopics: string[] = []): Topic[] {
    return this.getAllTopics().filter(topic => 
      this.canAccessTopic(topic.id, completedTopics)
    );
  }

  // Progress Tracking Integration
  getTopicProgress(topicId: string, lessonProgress: Record<string, any>): number {
    const topicLessons = this.getLessonsForTopic(topicId);
    if (topicLessons.length === 0) return 0;

    const totalProgress = topicLessons.reduce((sum, lesson) => {
      const progress = lessonProgress[lesson.id];
      return sum + (progress?.progress || 0);
    }, 0);

    return Math.round(totalProgress / topicLessons.length);
  }

  isTopicCompleted(topicId: string, lessonProgress: Record<string, any>): boolean {
    return this.getTopicProgress(topicId, lessonProgress) === 100;
  }

  getLearningPathProgress(pathId: string, lessonProgress: Record<string, any>): number {
    const path = this.getLearningPath(pathId);
    if (!path) return 0;

    const totalProgress = path.topics.reduce((sum, topicId) => {
      return sum + this.getTopicProgress(topicId, lessonProgress);
    }, 0);

    return Math.round(totalProgress / path.topics.length);
  }

  isLearningPathCompleted(pathId: string, lessonProgress: Record<string, any>): boolean {
    const path = this.getLearningPath(pathId);
    if (!path) return false;

    return path.topics.every(topicId => this.isTopicCompleted(topicId, lessonProgress));
  }

  // Content Search and Filtering
  createContentIndex(lessons: Lesson[]): ContentIndex[] {
    return lessons.map(lesson => {
      const topics = this.getTopicsForLesson(lesson.id);
      const searchableText = [
        lesson.title,
        lesson.description,
        ...topics.map(t => t.name),
        ...topics.flatMap(t => t.tags),
        ...topics.flatMap(t => t.learningObjectives)
      ].join(' ').toLowerCase();

      return {
        id: lesson.id,
        type: 'lesson',
        title: lesson.title,
        description: lesson.description,
        topics: topics.map(t => t.id),
        difficulty: lesson.difficulty,
        estimatedDuration: parseInt(lesson.duration) || 0,
        tags: [...new Set(topics.flatMap(t => t.tags))],
        keywords: [...new Set([lesson.category, ...topics.map(t => t.name)])],
        lastUpdated: new Date().toISOString(),
        version: '1.0.0',
        searchableText
      };
    });
  }

  searchContent(query: string, filters: SearchFilter = {}): SearchResult[] {
    const contentIndex = this.createContentIndex(this.lessons);
    const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);

    let results: SearchResult[] = contentIndex.map(content => {
      let relevanceScore = 0;
      const matchHighlights: { field: string; fragments: string[] }[] = [];

      // Calculate relevance score based on search terms
      searchTerms.forEach(term => {
        // Title matches (highest weight)
        if (content.title.toLowerCase().includes(term)) {
          relevanceScore += 10;
          matchHighlights.push({
            field: 'title',
            fragments: [content.title]
          });
        }

        // Description matches
        if (content.description.toLowerCase().includes(term)) {
          relevanceScore += 5;
          matchHighlights.push({
            field: 'description',
            fragments: [content.description]
          });
        }

        // Topic matches
        if (content.topics.some(topicId => {
          const topic = this.getTopic(topicId);
          return topic?.name.toLowerCase().includes(term);
        })) {
          relevanceScore += 7;
        }

        // Tag matches
        if (content.tags.some(tag => tag.toLowerCase().includes(term))) {
          relevanceScore += 3;
        }
      });

      return {
        content,
        relevanceScore,
        matchHighlights
      };
    });

    // Apply filters
    results = results.filter(result => {
      const { content } = result;

      if (filters.topics && !filters.topics.some(topic => content.topics.includes(topic))) {
        return false;
      }

      if (filters.difficulty && !filters.difficulty.includes(content.difficulty)) {
        return false;
      }

      if (filters.type && !filters.type.includes(content.type)) {
        return false;
      }

      if (filters.duration) {
        if (filters.duration.min && content.estimatedDuration < filters.duration.min) {
          return false;
        }
        if (filters.duration.max && content.estimatedDuration > filters.duration.max) {
          return false;
        }
      }

      if (filters.tags && !filters.tags.some(tag => content.tags.includes(tag))) {
        return false;
      }

      return true;
    });

    // Sort by relevance score and return top results
    return results
      .filter(result => result.relevanceScore > 0)
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, 20);
  }

  // Curriculum Analytics
  getCurriculumStats() {
    return {
      ...this.curriculum.metadata,
      topicDistribution: {
        foundations: this.getTopicsByParent('foundations').length,
        harmony: this.getTopicsByParent('harmony').length,
        psychology: this.getTopicsByParent('psychology').length,
        application: this.getTopicsByParent('application').length
      },
      learningPathDistribution: {
        foundations: this.getLearningPathsByCategory('foundations').length,
        practical: this.getLearningPathsByCategory('practical').length,
        advanced: this.getLearningPathsByCategory('advanced').length,
        specialized: this.getLearningPathsByCategory('specialized').length
      }
    };
  }

  // Skill Assessment Integration
  assessSkillLevel(skillId: string, lessonProgress: Record<string, any>): 1 | 2 | 3 | 4 | 5 {
    // Simple skill assessment based on completed topics
    const relatedTopics = this.getAllTopics().filter(topic => 
      topic.skills.includes(skillId)
    );

    if (relatedTopics.length === 0) return 1;

    const completedTopics = relatedTopics.filter(topic => 
      this.isTopicCompleted(topic.id, lessonProgress)
    );

    const completionRatio = completedTopics.length / relatedTopics.length;

    if (completionRatio >= 0.9) return 5;
    if (completionRatio >= 0.7) return 4;
    if (completionRatio >= 0.5) return 3;
    if (completionRatio >= 0.3) return 2;
    return 1;
  }

  getRecommendedNextTopics(currentTopics: string[], lessonProgress: Record<string, any>): Topic[] {
    const availableTopics = this.getAvailableTopics(currentTopics);
    
    return availableTopics
      .filter(topic => !currentTopics.includes(topic.id))
      .filter(topic => this.getTopicProgress(topic.id, lessonProgress) < 100)
      .sort((a, b) => {
        // Prioritize topics with more prerequisites met
        const aPrereqMet = a.prerequisites.filter(p => currentTopics.includes(p)).length;
        const bPrereqMet = b.prerequisites.filter(p => currentTopics.includes(p)).length;
        
        if (aPrereqMet !== bPrereqMet) {
          return bPrereqMet - aPrereqMet;
        }
        
        // Then by difficulty (easier first)
        const difficultyOrder = { 'beginner': 0, 'intermediate': 1, 'advanced': 2 };
        return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
      });
  }
}

// Export singleton instance
export const curriculumService = new CurriculumService([]); // Will be initialized with actual lessons
